import React, { useEffect } from 'react';
import { useEntityPicker } from './useEntityPicker';

/**
 * Generic EntityPickerSelect
 * @param {boolean} allowAll - If true, ID 0 acts as "All". If false, the first option is a "Select" placeholder.
 */
export function EntityPickerSelect({
  config,
  value,
  onChange,
  disabled = false,
  placeholder = "--Select Data--",
  allowAll = true,      // New Toggle
  allLabel = "All",     // Label specifically for the "All" option
  className = "",
}) {
  const { entities, loading, fetchData } = useEntityPicker(config);

  useEffect(() => {
    fetchData();
  }, []);

  const isPrimitiveMode = config.valueType === "primitive";
  const primaryField = isPrimitiveMode ? null : config.displayFields.primary;

  // Logic to determine if a real entity is selected
  // If allowAll is false, we strictly check for value.id
  // MODIFIED: Handle both object mode ({id, ...}) and primitive mode ("2026-09-10")
  const isSelected = isPrimitiveMode
    ? value !== null && value !== undefined && value !== ""
    : value && value.id !== 0 && value.id !== null && value.id !== undefined;

  // The "empty" value depends on allowAll
  // If allowAll is true, we use "0" as the default
  // If allowAll is false, we use "" so it remains blank
  const defaultValue = allowAll ? "0" : "";

  return (
    <select
      className={`
                w-full border border-gray-300 rounded-md px-3 h-[33px] transition-all cursor-pointer
                focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500
                ${!isSelected ? 'text-gray-400 font-normal' : 'text-gray-700 font-medium border-blue-200 bg-blue-50/10'}
                ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white hover:border-gray-400'}
                ${className}
            `}
      // MODIFIED: primitive values are used directly; objects still use .id
      value={isPrimitiveMode ? (value ?? defaultValue) : (value?.id || defaultValue)}
      disabled={loading || disabled}
      // MODIFIED: primitive mode emits the raw value; object mode emits the matched entity
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "0" || raw === "") {
          onChange(null);
          return;
        }
        if (isPrimitiveMode) {
          onChange(raw);
        } else {
          const selectedObj = entities.find(o => o.id.toString() === raw.toString());
          onChange(selectedObj);
        }
      }}
    >
      {/* 
        Placeholder Option:
        - If allowAll is true: Acts as the "All" selector (value 0)
        - If allowAll is false: Acts as a "Select..." blank state (value "")
      */}
      <option value={defaultValue}>
        {loading ? 'Loading...' : (allowAll ? allLabel : placeholder)}
      </option>

      {entities.map((item) => (
        <option
          key={isPrimitiveMode ? item : item.id}            // MODIFIED: primitive items are the key
          value={isPrimitiveMode ? item : item.id}          // MODIFIED
          className="text-gray-800"
        >
          {isPrimitiveMode
            ? item
            : (item[primaryField] || item.name || item.description)}
        </option>
      ))}
    </select>
  );
}